import type { Facts } from './types';

/** How long a pending snapshot usually takes, in milliseconds, and whether it was measured */
export interface Expectation {
	median: number;
	p90: number;
	measured: boolean;
}

/**
 * How long a pending snapshot usually takes (decision D50): "usually about" is the measured median and
 * "taking longer than usual" starts past the 90th percentile, as the service measures them over completed
 * snapshots (`durations` of `GET /v1/interface`). Below the service's own number of measurements it says
 * nothing, and the engineering defaults `DEFAULT` stand instead.
 *
 * A measured 90th percentile is never taken below `FLOOR`: completions made by tools and suites take a few
 * seconds, and a person's snapshot is read again only every 10 s, so a bound of a few seconds would say
 * "taking longer than usual" before the page could have seen the snapshot complete. The median is kept as
 * measured.
 */
export class Usual {
	static DEFAULT = Object.freeze({ median: 30_000, p90: 120_000 });
	static FLOOR = 30_000;

	static of(durations: Facts['durations']): Expectation {
		const median = Number(durations?.median);
		const p90 = Number(durations?.p90);
		if (!Number.isFinite(median) || median <= 0 || !Number.isFinite(p90)) return { ...Usual.DEFAULT, measured: false };
		return { median, p90: Math.max(median, p90, Usual.FLOOR), measured: true };
	}
}
